/**
 * OnboardingChecklist Component
 *
 * Displays onboarding progress and checklist for new clients.
 * Shows completion percentage, required steps, and actionable items.
 *
 * Used in adaptive dashboard for clients with completion < 100%.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useCallback } from 'react';
import { Card, Progress, Badge, Button, Tooltip } from 'flowbite-react';
import {
  HiCheckCircle,
  HiCode,
  HiDocumentAdd,
  HiDocumentText,
  HiSearch,
  HiUserGroup,
  HiCog,
  HiCreditCard,
  HiClipboardCheck,
  HiArrowRight,
  HiClock,
  HiStar,
} from 'react-icons/hi';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import type { OnboardingProgress, OnboardingStep } from '../api/onboarding';
import { getProgressColor } from '../api/onboarding';

// =============================================================================
// TYPES
// =============================================================================

interface OnboardingChecklistProps {
  progress: OnboardingProgress;
  onActionClick: (step: OnboardingStep) => void;
  onStepComplete?: (stepId: string) => void;
  compact?: boolean;
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'HiCode': HiCode,
  'HiDocumentAdd': HiDocumentAdd,
  'HiDocumentText': HiDocumentText,
  'HiSearch': HiSearch,
  'HiUserGroup': HiUserGroup,
  'HiCog': HiCog,
  'HiCreditCard': HiCreditCard,
  'HiClipboardCheck': HiClipboardCheck,
  'HiCheckCircle': HiCheckCircle,
};

// Step ID to icon mapping
const stepIconMap: Record<string, string> = {
  'connect-repo': 'HiCode',
  'upload-docs': 'HiDocumentAdd',
  'create-spec': 'HiDocumentText',
  'run-scan': 'HiSearch',
  'invite-team': 'HiUserGroup',
  'configure-settings': 'HiCog',
  'setup-billing': 'HiCreditCard',
  'review-assessment': 'HiClipboardCheck',
};

// =============================================================================
// COMPONENT
// =============================================================================

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  progress,
  onActionClick,
  onStepComplete,
  compact = false,
}) => {
  // Get icon component for a step
  const getStepIcon = useCallback((step: OnboardingStep) => {
    const iconName = step.icon || stepIconMap[step.step_id] || 'HiCheckCircle';
    const IconComponent = iconMap[iconName] || HiCheckCircle;
    return IconComponent;
  }, []);

  // Render a single step item
  const renderStepItem = useCallback((step: OnboardingStep) => {
    const IconComponent = getStepIcon(step);
    const isCompleted = step.is_completed;

    return (
      <div
        key={step.step_id}
        className={`flex items-start gap-4 p-4 rounded-lg border transition-all ${
          isCompleted
            ? 'bg-green-50 border-green-200'
            : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-sm'
        }`}
      >
        {/* Status Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
          {isCompleted ? (
            <HiCheckCircle className="w-6 h-6" />
          ) : (
            <HiOutlineCheckCircle className="w-6 h-6" />
          )}
        </div>

        {/* Step Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <IconComponent className={`w-4 h-4 ${isCompleted ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`font-medium ${isCompleted ? 'text-green-800' : 'text-gray-900'}`}>
              {step.step_name}
            </span>
            {step.is_required && !isCompleted && (
              <Tooltip content="Required step">
                <Badge color="warning" size="xs">
                  <HiStar className="w-3 h-3 mr-1 inline" />
                  Required
                </Badge>
              </Tooltip>
            )}
            {isCompleted && step.completed_at && (
              <span className="text-xs text-green-600">
                Completed {new Date(step.completed_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className={`text-sm ${isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
            {step.step_description}
          </p>
        </div>

        {/* Action Button */}
        {!isCompleted && (
          <div className="flex-shrink-0">
            <Button
              size="sm"
              color="light"
              onClick={() => onActionClick(step)}
            >
              Start <HiArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    );
  }, [getStepIcon, onActionClick]);

  // Sort steps: incomplete required first, then incomplete optional, then completed
  const sortedSteps = [...progress.steps].sort((a, b) => {
    // Completed steps go to the end
    if (a.is_completed !== b.is_completed) {
      return a.is_completed ? 1 : -1;
    }
    // Among incomplete, required steps come first
    if (!a.is_completed && !b.is_completed) {
      if (a.is_required !== b.is_required) {
        return a.is_required ? -1 : 1;
      }
    }
    // Then sort by order
    return a.step_order - b.step_order;
  });

  const progressColor = getProgressColor(progress.completion_percentage);

  // Compact view for sidebar or small spaces
  if (compact) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Getting Started</h3>
          <Badge color={progressColor}>
            {progress.completion_percentage}%
          </Badge>
        </div>
        <Progress
          progress={progress.completion_percentage}
          color={progressColor}
          size="lg"
          className="mb-4"
        />
        <p className="text-sm text-gray-600 mb-3">
          {progress.completed_steps} of {progress.total_steps} steps completed
        </p>
        {/* Show first incomplete required step */}
        {sortedSteps.filter(s => !s.is_completed && s.is_required).slice(0, 1).map(step => (
          <Button
            key={step.step_id}
            color="blue"
            className="w-full"
            onClick={() => onActionClick(step)}
          >
            {step.step_name} <HiArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ))}
      </Card>
    );
  }

  // Full view
  return (
    <Card>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Welcome to Equilateral, {progress.Client_Name}!
          </h2>
          <p className="text-gray-600">
            Complete these steps to get the most out of the platform.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-indigo-600">
            {progress.completion_percentage}%
          </div>
          <div className="text-sm text-gray-500">complete</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <span className="text-sm text-gray-600">
            {progress.completed_steps} of {progress.total_steps} steps completed
          </span>
          {progress.estimated_time_remaining_minutes && progress.estimated_time_remaining_minutes > 0 && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <HiClock className="w-4 h-4" />
              ~{progress.estimated_time_remaining_minutes} min remaining
            </span>
          )}
        </div>
        <Progress
          progress={progress.completion_percentage}
          color={progressColor}
          size="lg"
        />
      </div>

      {/* Required Steps Summary */}
      {progress.required_steps_completed < progress.total_required_steps && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-yellow-800">
            <HiStar className="w-5 h-5 text-yellow-600" />
            <span className="font-medium">
              {progress.total_required_steps - progress.required_steps_completed} required {progress.total_required_steps - progress.required_steps_completed === 1 ? 'step' : 'steps'} remaining
            </span>
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            Complete required steps to unlock all platform features.
          </p>
        </div>
      )}

      {/* Steps List */}
      <div className="space-y-3">
        {sortedSteps.map(renderStepItem)}
      </div>

      {/* Completion Message */}
      {progress.completion_percentage === 100 && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
          <HiCheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-green-800">All Done!</h3>
          <p className="text-green-700">
            You've completed all onboarding steps. Your dashboard will now show your health overview.
          </p>
        </div>
      )}
    </Card>
  );
};

export default OnboardingChecklist;
